/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable unused-imports/no-unused-vars */
import { createSlice, SliceCaseReducers,  AnyAction, PayloadAction } from "@reduxjs/toolkit";
import { getErrorMessage } from "../utils/common";

interface SliceConfig<T extends StateSlice> {
  name: string;
  initialState: T;
  actions: Partial<Record<keyof T, ActionGroup<T>>>;
}

// Utility functions to handle loading and error state
const setLoadingState = <T extends StateSlice>(state: T, key: keyof T, isLoading = true) => {
  if (state[key] && typeof state[key] === "object") {
    state[key] = { ...state[key], isLoading, isError: false, error: "" };
  }
};

const setErrorState = <T extends StateSlice>(state: T, key: keyof T, error: string) => {
  if (state[key] && typeof state[key] === "object") {
    state[key] = { ...state[key], isLoading: false, isError: true, error };
  }
};

// **Generic Redux Slice Factory**
export const createModuleSlice = <T extends StateSlice>({
  name,
  initialState,
  actions,
  reducers,
  extraReducers
}: SliceConfig<T>) => {
  return createSlice<T, SliceCaseReducers<T>>({
    name,
    initialState,
    reducers: {
      setPaginationModel: (state, { payload }: PayloadAction<Partial<T["paginationModel"]>>) => {
        if ("paginationModel" in state && typeof state.paginationModel === "object") {
          state.paginationModel = { ...state.paginationModel, ...payload };
        }
      },
      setOptionsData: (state, { payload }: PayloadAction<{ name: string; value: any }>) => {
        if ("optionsData" in state && typeof state.optionsData === "object") {
          state.optionsData = { ...state.optionsData, ...payload}
        }
      },
      setStaticOptions: (state, { payload }) =>  {
        state.staticOptions = {...state.staticOptions, ...payload}
      },
      resetState: () => initialState,
      ...reducers
    },
    extraReducers: (builder) => {
      Object.entries(actions).forEach(([stateKey, actionGroup]) => {
        if (actionGroup) {
          Object.entries(actionGroup).forEach(([actionName, action]) => {
            if (action && "pending" in action && "fulfilled" in action && "rejected" in action) {
              // Handle AsyncThunk Actions
              builder
                .addCase(action.pending, (state) => setLoadingState(state, stateKey as keyof T))
                .addCase(action.fulfilled, (state, { payload }: PayloadAction<any>) => {
                  if (state[stateKey as keyof T] && typeof state[stateKey as keyof T] === "object") {
                    state[stateKey as keyof T] = {
                      ...state[stateKey as keyof T],
                      isLoading: false,
                      ...(payload?.rows ? { rows: payload.rows } : {}),
                      ...(payload?.data ? { data: payload.data } : {}),
                      ...(payload?.fields ? { fields: payload.fields } : {}),
                      ...(payload?.activityLogs ? { activityLogs: payload.activityLogs } : {}),
                      ...(payload?.pagination ? { pagination: payload.pagination } : {}),
                    };
                    if(payload?.pagination) {
                      state.paginationModel = { ...state.paginationModel, ...payload.pagination }
                    }
                  }
                })
                .addCase(action.rejected, (state, { error }: AnyAction) =>{

                  setErrorState(state, stateKey as keyof T, getErrorMessage(error?.message))
                }
                );
            }
          });
        }
      });
      if (extraReducers) {
        extraReducers(builder);
      }
    },
   
  });
};
